// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Bootstrap + orchestration. Resolves the room (from the link hash or freshly
 * minted), joins the mesh, and wires the board CRDT ↔ the drawing canvas ↔ the
 * transport ↔ the UI. Drawing ops are tiny AES-GCM blobs and the board is bounded,
 * so everything runs on the main thread; the transport itself is off-thread inside
 * trystero.
 */
import './styles/main.css';
import QRCode from 'qrcode';
import { AppUI, PALETTE, WIDTHS } from './ui.ts';
import { initModals, toast } from './modals.ts';
import { initGlossary } from './glossary.ts';
import { emit } from './eventlog.ts';
import { readInvite, writeRoomHash, currentInviteUrl } from './router.ts';
import { generateRoomId, generatePassphrase, normalizePass, normalizeName } from './room.ts';
import { deriveMessageKey, deriveSasWords, encryptBytes, decryptBytes } from './crypto.ts';
import { encodeOp, decodeOp, encodeOps, decodeOps, packFrame, unpackFrame } from './wire.ts';
import { hueForId } from './color.ts';
import { WORDS } from './wordlist.ts';
import { Board, BOARD_W, BOARD_H } from './board.ts';
import { BoardCanvas, type Draft } from './canvas.ts';
import { boardToSvg, paintBoard, fitBoard } from './draw.ts';
import { exampleDoodle } from './sample.ts';
import { BoardSession, type Hello } from './session.ts';
import type { Cursor, Member, Op, Shape, Status, Tool } from './types.ts';

const LS_NAME = 'sketchwell.name';
const LS_TOOL = 'sketchwell.tool';
const LS_COLOR = 'sketchwell.color';
const LS_WIDTH = 'sketchwell.width';
const CURSOR_SEND_MS = 50;
const CURSOR_STALE_MS = 4000;

let room = '';
let pass = '';
let displayName = '';
let tool: Tool = 'pen';
let color = PALETTE[0];
let width = WIDTHS[1];

let session: BoardSession | null = null;
let msgKey: CryptoKey | null = null;
let qrRendered = false;

const board = new Board();
const members = new Map<string, Member>();
const cursors = new Map<string, Cursor>();
const undoStack: Shape[] = [];

let lastCursorSent = 0;

const store = safeStorage();

const ui = new AppUI(document.getElementById('app') as HTMLElement, {
  onToolChange: setTool,
  onColorChange: setColor,
  onWidthChange: setWidth,
  onUndo: undo,
  onClear: clearBoard,
  onExample: addExample,
  onExportPng: () => void exportPng(),
  onExportSvg: () => void exportSvg(),
  onCopyLink: copyLink,
  onShareLink: shareLink,
  onToggleQr: toggleQr,
  onNameChange: changeName,
  onLeave: leaveRoom,
});

const canvas = new BoardCanvas(ui.boardWrap(), board, {
  onDraftStream: streamDraft,
  onDraftCommit: commitDraft,
  onErase: eraseShape,
  onCursor: onLocalCursor,
});

init();

function init(): void {
  initModals();
  initGlossary();

  displayName = store.getItem(LS_NAME) || randomGuestName();
  ui.setName(displayName);

  tool = (store.getItem(LS_TOOL) as Tool) || 'pen';
  color = store.getItem(LS_COLOR) || PALETTE[0];
  const w = Number(store.getItem(LS_WIDTH));
  width = WIDTHS.includes(w) ? w : WIDTHS[1];
  applyTool();
  applyColor();
  applyWidth();

  const invite = readInvite();
  if (invite && invite.pass) {
    room = invite.room;
    pass = normalizePass(invite.pass);
  } else {
    if (invite && !invite.pass) toast('That link was missing its passphrase — opened a fresh board.', 'err');
    room = generateRoomId();
    pass = generatePassphrase();
    writeRoomHash(room, pass, true);
  }

  window.addEventListener('pagehide', () => session?.leave());
  window.addEventListener('beforeunload', () => session?.leave());
  setInterval(cursorTick, 120);

  void connect();
  installTestHook();
}

async function connect(): Promise<void> {
  ui.setStatus('connecting', 1);
  const url = currentInviteUrl(room, pass, true);
  ui.setInviteLink(url);
  void renderSas();

  members.clear();
  members.set(selfIdOrTemp(), { id: selfIdOrTemp(), name: displayName, hue: hueForId(selfIdOrTemp()) });

  try {
    msgKey = await deriveMessageKey(pass, room);
  } catch (e) {
    emit('bad', `Key derivation failed: ${(e as Error).message}`);
    toast('Could not set up encryption for this board.', 'err');
    ui.setStatus('error', 1);
    return;
  }

  session = new BoardSession(room, pass, {
    onPeerJoin: handlePeerJoin,
    onPeerLeave: handlePeerLeave,
    onHello: handleHello,
    onOp: handleRemoteOp,
    onSync: handleRemoteSync,
    onCursor: handleRemoteCursor,
    onError: (m) => {
      emit('bad', m);
      toast(m, 'err');
      ui.setStatus('error', members.size);
    },
  });
  const ok = session.join();
  if (ok) {
    reseedSelf();
    ui.setStatus('waiting', members.size);
    renderMembers();
    emit('info', 'Board open. Share the invite link to draw together.');
  }
}

function selfIdOrTemp(): string {
  return session?.selfId ?? 'self';
}
function reseedSelf(): void {
  const real = selfIdOrTemp();
  if (!members.has(real)) {
    members.delete('self');
    members.set(real, { id: real, name: displayName, hue: hueForId(real) });
  }
}

// ── presence ──────────────────────────────────────────────────────────────────
function handlePeerJoin(peerId: string): void {
  ensureMember(peerId);
  session?.sendHello({ name: displayName }, peerId);
  // Send the newcomer the whole board so they catch up.
  void sendSyncTo(peerId);
  ui.setStatus('live', members.size);
  renderMembers();
  emit('good', 'Someone joined the board.');
}

function handlePeerLeave(peerId: string): void {
  const m = members.get(peerId);
  members.delete(peerId);
  cursors.delete(peerId);
  renderMembers();
  ui.setStatus(members.size > 1 ? 'live' : 'waiting', members.size);
  emit('warn', `${m?.name || 'Someone'} left the board.`);
}

function handleHello(peerId: string, hello: Hello): void {
  const name = normalizeName(hello?.name || '') || 'Guest';
  ensureMember(peerId).name = name;
  renderMembers();
}

function ensureMember(peerId: string): Member {
  let m = members.get(peerId);
  if (!m) {
    m = { id: peerId, name: 'Guest', hue: hueForId(peerId) };
    members.set(peerId, m);
  }
  return m;
}

function renderMembers(): void {
  const self = selfIdOrTemp();
  const list = [...members.values()].sort((a, b) => (a.id === self ? -1 : b.id === self ? 1 : a.name.localeCompare(b.name)));
  ui.renderMembers(list, self);
}

// ── receiving ops ───────────────────────────────────────────────────────────
async function handleRemoteOp(_peerId: string, frame: Uint8Array): Promise<void> {
  if (!msgKey) return;
  try {
    const { iv, cipher } = unpackFrame(frame);
    const plain = await decryptBytes(msgKey, iv, cipher);
    const op = decodeOp(plain);
    if (board.apply(op)) canvas.render();
    if (op.t === 'clear') emit('warn', 'The board was cleared.');
  } catch (e) {
    emit('bad', `Could not apply a received op: ${(e as Error).message}`);
  }
}

async function handleRemoteSync(peerId: string, frame: Uint8Array): Promise<void> {
  if (!msgKey) return;
  try {
    const { iv, cipher } = unpackFrame(frame);
    const plain = await decryptBytes(msgKey, iv, cipher);
    const ops = decodeOps(plain);
    if (board.applyMany(ops)) canvas.render();
    const name = members.get(peerId)?.name || 'a peer';
    emit('good', `Synced the board from ${name} (${board.count()} shapes).`);
  } catch (e) {
    emit('bad', `Could not apply the board sync: ${(e as Error).message}`);
  }
}

function handleRemoteCursor(peerId: string, x: number, y: number): void {
  ensureMember(peerId);
  cursors.set(peerId, { x, y, at: Date.now() });
}

// ── local drawing → CRDT + broadcast ──────────────────────────────────────────
function stampShape(draft: Draft): Shape {
  return { ...draft, author: selfIdOrTemp(), lamport: board.tick() };
}

async function broadcastOp(op: Op): Promise<void> {
  if (!msgKey || !session) return;
  const { iv, cipher } = await encryptBytes(msgKey, encodeOp(op));
  session.broadcastOp(packFrame(iv, cipher));
}

/** A live, still-drawing stroke — broadcast so peers see it appear in real time. */
function streamDraft(draft: Draft): void {
  const shape = stampShape(draft);
  void broadcastOp({ t: 'add', shape });
}

/** A finished shape — commit locally, then broadcast the authoritative op. */
function commitDraft(draft: Draft): void {
  const shape = stampShape(draft);
  board.apply({ t: 'add', shape });
  canvas.render();
  undoStack.push(shape);
  void broadcastOp({ t: 'add', shape });
  emit('xfer', `Added a ${shape.kind}.`);
}

function eraseShape(id: string): void {
  const op: Op = { t: 'remove', id, author: selfIdOrTemp(), lamport: board.tick() };
  if (board.apply(op)) canvas.render();
  void broadcastOp(op);
}

function undo(): void {
  // Remove the most recent of our own shapes that is still on the board.
  while (undoStack.length) {
    const shape = undoStack.pop() as Shape;
    if (!board.get(shape.id)) continue;
    eraseShape(shape.id);
    emit('info', 'Undid your last shape.');
    return;
  }
  toast('Nothing of yours left to undo.', 'info');
}

function clearBoard(): void {
  if (board.isEmpty()) {
    toast('The board is already empty.', 'info');
    return;
  }
  if (!confirm('Clear the whole board for everyone? This cannot be undone.')) return;
  const op: Op = { t: 'clear', author: selfIdOrTemp(), lamport: board.tick() };
  board.apply(op);
  undoStack.length = 0;
  canvas.render();
  void broadcastOp(op);
  emit('warn', 'You cleared the board.');
}

function addExample(): void {
  for (const s of exampleDoodle()) commitDraft({ id: exampleId(), ...s });
  toast('Example doodle added — try exporting it.', 'ok');
}

let exampleCounter = 0;
function exampleId(): string {
  exampleCounter += 1;
  return `ex-${selfIdOrTemp()}-${exampleCounter}`;
}

function onLocalCursor(x: number, y: number): void {
  const now = Date.now();
  if (now - lastCursorSent < CURSOR_SEND_MS) return;
  lastCursorSent = now;
  session?.sendCursor(x, y);
}

function cursorTick(): void {
  const now = Date.now();
  let changed = false;
  for (const [id, c] of cursors) {
    if (now - c.at > CURSOR_STALE_MS) {
      cursors.delete(id);
      changed = true;
    }
  }
  ui.renderCursors(cursors, members, canvas.currentFit());
  if (changed) {
    /* pruned */
  }
}

// ── tool state ────────────────────────────────────────────────────────────────
function setTool(t: Tool): void {
  tool = t;
  store.setItem(LS_TOOL, t);
  applyTool();
}
function applyTool(): void {
  canvas.setTool(tool);
  ui.setActiveTool(tool);
}
function setColor(c: string): void {
  color = c;
  store.setItem(LS_COLOR, c);
  applyColor();
}
function applyColor(): void {
  canvas.setColor(color);
  ui.setActiveColor(color);
}
function setWidth(w: number): void {
  width = w;
  store.setItem(LS_WIDTH, String(w));
  applyWidth();
}
function applyWidth(): void {
  canvas.setWidth(width);
  ui.setActiveWidth(width);
}

function changeName(raw: string): void {
  const name = normalizeName(raw) || randomGuestName();
  if (name === displayName) return;
  displayName = name;
  store.setItem(LS_NAME, name);
  ui.setName(name);
  const self = members.get(selfIdOrTemp());
  if (self) self.name = name;
  renderMembers();
  session?.sendHello({ name });
}

// ── export (the takeaway artefacts) ────────────────────────────────────────────
async function exportPng(): Promise<void> {
  if (board.isEmpty()) {
    toast('The board is empty — draw something first.', 'err');
    return;
  }
  const scale = 2;
  const c = document.createElement('canvas');
  c.width = BOARD_W * scale;
  c.height = BOARD_H * scale;
  const ctx = c.getContext('2d') as CanvasRenderingContext2D;
  paintBoard(ctx, board.visible(), BOARD_W * scale, BOARD_H * scale, 1, fitBoard(BOARD_W * scale, BOARD_H * scale));
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/png'));
  if (!blob) {
    toast('Could not render the PNG.', 'err');
    return;
  }
  await deliverFile(blob, `sketchwell-${room}.png`, 'image/png');
  emit('info', 'Exported the board as PNG.');
}

async function exportSvg(): Promise<void> {
  if (board.isEmpty()) {
    toast('The board is empty — draw something first.', 'err');
    return;
  }
  const svg = boardToSvg(board.visible());
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  await deliverFile(blob, `sketchwell-${room}.svg`, 'image/svg+xml');
  emit('info', 'Exported the board as SVG.');
}

async function deliverFile(blob: Blob, name: string, mime: string): Promise<void> {
  const file = new File([blob], name, { type: mime });
  const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: 'Sketchwell board' });
      toast('Shared.', 'ok');
      return;
    } catch {
      /* cancelled → fall through to save */
    }
  }
  const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({ suggestedName: name });
      const w = await (handle as FileSystemFileHandle).createWritable();
      await w.write(blob);
      await w.close();
      toast('Saved.', 'ok');
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
    }
  }
  anchorDownload(blob, name);
  toast('Saved.', 'ok');
}

// ── invite link + QR ────────────────────────────────────────────────────────
async function copyLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(currentInviteUrl(room, pass, true));
    toast('Invite link copied.', 'ok');
  } catch {
    toast('Could not copy — select the link and copy manually.', 'err');
  }
}

async function shareLink(): Promise<void> {
  const url = currentInviteUrl(room, pass, true);
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Sketchwell', text: 'Draw with me on this private whiteboard', url });
    } catch {
      /* cancelled */
    }
  } else {
    void copyLink();
  }
}

function toggleQr(): void {
  if (!qrRendered) {
    void renderQr(currentInviteUrl(room, pass, true));
    qrRendered = true;
  }
  ui.toggleQr();
}

async function renderQr(url: string): Promise<void> {
  try {
    const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 320, color: { dark: '#1f2430', light: '#ffffff' } });
    ui.setQr(dataUrl);
  } catch {
    /* QR is a nicety */
  }
}

async function renderSas(): Promise<void> {
  try {
    ui.setSas(await deriveSasWords(pass, room));
  } catch {
    /* ignore */
  }
}

// ── room controls ─────────────────────────────────────────────────────────────
function leaveRoom(): void {
  session?.leave();
  session = null;
  cursors.clear();
  ui.renderCursors(cursors, members, canvas.currentFit());
  emit('warn', 'You left the board. Opened a fresh one.');
  // Reset local state + mint a new ephemeral board.
  location.hash = '';
  room = generateRoomId();
  pass = generatePassphrase();
  qrRendered = false;
  undoStack.length = 0;
  resetBoard();
  writeRoomHash(room, pass, true);
  void connect();
}

/** Replace the board with an empty one and repaint (used on leave). */
function resetBoard(): void {
  // A fresh Board — reassign the module field via a clear barrier is not enough
  // (a new room has a new key), so we drop all state by clearing above the clock.
  board.apply({ t: 'clear', author: selfIdOrTemp(), lamport: board.now() + 1_000_000 });
  canvas.render();
}

async function sendSyncTo(peerId: string): Promise<void> {
  if (!msgKey || !session) return;
  const ops = board.snapshotOps();
  if (ops.length === 0) return;
  try {
    const { iv, cipher } = await encryptBytes(msgKey, encodeOps(ops));
    session.sendSync(packFrame(iv, cipher), peerId);
  } catch (e) {
    emit('bad', `Could not send the board sync: ${(e as Error).message}`);
  }
}

// ── keyboard shortcuts ──────────────────────────────────────────────────────
window.addEventListener('keydown', (e) => {
  const target = e.target as HTMLElement;
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
    e.preventDefault();
    undo();
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const map: Record<string, Tool> = { p: 'pen', l: 'line', a: 'arrow', r: 'rect', o: 'ellipse', t: 'text', e: 'eraser' };
  const t = map[e.key.toLowerCase()];
  if (t) setTool(t);
});

// ── helpers ─────────────────────────────────────────────────────────────────
function anchorDownload(blob: Blob, name: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

function randomGuestName(): string {
  const w = WORDS[Math.floor(Math.random() * WORDS.length)];
  return w.charAt(0).toUpperCase() + w.slice(1);
}

function safeStorage(): { getItem: (k: string) => string | null; setItem: (k: string, v: string) => void } {
  try {
    const k = '__sketchwell_probe';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return { getItem: (key) => mem.get(key) ?? null, setItem: (key, v) => void mem.set(key, v) };
  }
}

// ── automated two-tab test hook ─────────────────────────────────────────────
// The preview browser can't test a real multi-device board, but two tabs joining
// the same room over the real relays proves discovery + mesh connect +
// encrypt/decrypt + CRDT convergence end-to-end. Drives REAL transport + crypto.
function installTestHook(): void {
  (window as unknown as { __sketchwellTest?: unknown }).__sketchwellTest = {
    room: () => room,
    pass: () => pass,
    selfId: () => selfIdOrTemp(),
    here: () => members.size,
    memberNames: () => [...members.values()].map((m) => m.name),
    status: (): Status | 'connected' => (members.size > 1 ? 'connected' : 'waiting'),
    sas: () => deriveSasWords(pass, room),
    count: () => board.count(),
    example: () => addExample(),
    stroke: (kind: Tool = 'pen', pts?: number[]) =>
      commitDraft({ id: `t-${selfIdOrTemp()}-${Math.random().toString(36).slice(2)}`, kind: kind as Shape['kind'], color, width, points: pts ?? [200, 200, 400, 300, 600, 250] }),
    clear: () => {
      const op: Op = { t: 'clear', author: selfIdOrTemp(), lamport: board.tick() };
      board.apply(op);
      canvas.render();
      void broadcastOp(op);
    },
    undo: () => undo(),
    svg: () => boardToSvg(board.visible()),
  };
}
